import {
  Bell,
  CreditCard,
  KeyRound,
  LayoutDashboard,
  Link2,
  LogOut,
  Mail,
  Package,
  ScrollText,
  User,
  Users,
  X,
} from 'lucide-react'
import { type ElementType } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useMockAuth } from '@/hooks/useMockAuth'
import { PATHS } from '@/routes/paths'
import { cn } from '@/utils/cn'

interface SidebarProps {
  open: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  icon: ElementType
  href: string
  end?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, href: PATHS.dashboard, end: true },
  { label: 'Connects', icon: Link2, href: PATHS.connects },
  { label: 'Users', icon: Users, href: PATHS.users },
  { label: 'Payment Gateways', icon: CreditCard, href: PATHS.paymentGateways },
  { label: 'Plans', icon: Package, href: PATHS.plans },
  { label: 'Email Configuration', icon: Mail, href: PATHS.emailConfig },
  { label: 'Login Logs', icon: ScrollText, href: PATHS.loginLogs },
  { label: 'Profile', icon: User, href: PATHS.profile },
  { label: 'Change Password', icon: KeyRound, href: PATHS.changePassword },
  { label: 'Notifications', icon: Bell, href: PATHS.notifications },
]

export function Sidebar({ open, onClose }: SidebarProps) {
  const { logout } = useMockAuth()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate(PATHS.login, { replace: true })
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
      isActive
        ? 'bg-sidebar-accent text-white'
        : 'text-sidebar-foreground hover:bg-sidebar-active hover:text-white',
    )

  const sidebarContent = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center justify-between border-b border-white/10 px-5">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-sm font-bold text-white">
            P
          </div>
          <span className="text-base font-bold text-white">Partner Portal</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-sidebar-foreground hover:bg-sidebar-active lg:hidden"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto p-4 scrollbar-thin">
        <ul className="space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon
            return (
              <li key={item.label}>
                <NavLink
                  to={item.href}
                  className={linkClass}
                  onClick={onClose}
                  end={item.end}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </NavLink>
              </li>
            )
          })}
        </ul>
      </nav>

      <div className="border-t border-white/10 p-4">
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-red-500/20 hover:text-red-300"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>
    </div>
  )

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 bg-sidebar transition-transform duration-300 lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {sidebarContent}
      </aside>
    </>
  )
}
